import { PaginatedResponse } from '../../models/paginated-response'
import { unifiedGet } from './unifiedApiRequest'

/**
 * Shape the unified opps (chains) summary endpoint returns. Besides the
 * universal `count` (chain count), it emits `places_count` (summed open doors of
 * those chains — the chains card's "Total Doors") and the product-aware,
 * filter-scoped `total_ltv`, so the flag-on chains card sources every stat here
 * instead of the legacy `/reports/chains/summary/`. It does NOT emit `alt_count`
 * (the doors summary is the one that carries the distinct-chain count); typing it
 * optional keeps the `?? 0` fallback in `useUnifiedTableActions` compiler-visible.
 */
export interface UnifiedOppsSummaryResponse {
  count: number
  // Always emitted by UnifiedOpportunitySummarySerializer (required fields).
  places_count: number
  total_ltv: number
  // The opps summary does NOT emit alt_count (the doors summary carries the
  // distinct-chain count); optional keeps the `?? 0` fallback compiler-visible.
  alt_count?: number
}

/**
 * Unified opportunities API methods (FIR-6995).
 *
 * Both endpoints take a backend `unified_target_type` (resolve app vertical
 * names through `getTargetType` in `targetTypeMap.ts` — never pass a raw vertical
 * slug here). The list returns the standard paginated shape `DECommonTable`
 * consumes; the summary returns `{ count }` (opps summary has no `alt_count` —
 * see `UnifiedOppsSummaryResponse`; the doors summary is the one that carries
 * the distinct-chain `alt_count`).
 *
 * Rows come back with shared display fields (`name`/`domain`, COALESCEd over CRM
 * overrides) plus that vertical's segment columns under their legacy field
 * names. Run rows through `adaptUnifiedItem` (`unifiedResponseAdapter.ts`) before
 * feeding the legacy columns, and outgoing params through `adaptUnifiedListParams`.
 */

/** A unified opportunities row: shared columns plus vertical segment columns. */
export type UnifiedOpportunityRow = Record<string, unknown>

/**
 * GET `/api/v1/opportunities/?target_type=<tt>&...` — paginated list of
 * opportunities for one vertical (shared + segment columns).
 */
export async function getOpportunitiesList(
  targetType: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<PaginatedResponse<UnifiedOpportunityRow>> {
  return unifiedGet<PaginatedResponse<UnifiedOpportunityRow>>(
    '/opportunities/',
    targetType,
    params,
    signal
  )
}

/**
 * GET `/api/v1/opportunities/summary/?target_type=<tt>&...` — aggregate counts
 * for the same filtered set, keeping stat cards in sync with the list.
 */
export async function getOpportunitiesSummary(
  targetType: string,
  params: Record<string, unknown> = {},
  signal?: AbortSignal
): Promise<UnifiedOppsSummaryResponse> {
  return unifiedGet<UnifiedOppsSummaryResponse>(
    '/opportunities/summary/',
    targetType,
    params,
    signal
  )
}
